import { z } from 'zod';
// Request shapes for the campaign surface. DUPLICATED, on purpose, into
// backend/src/schemas/campaigns.ts — `backend/src` cannot import
// @event67/shared because two CI workflows build the backend without building
// shared first. tests/contracts.test.ts pins the two copies together.
/** The COMPUTED attendee lifecycle status, which is what the admin filters by. */
export const CampaignAttendeeStatusSchema = z.enum([
    'not_invited',
    'invited',
    'delivered',
    'opened',
    'clicked',
    'joined',
    'bounced',
    'complained',
    'unsubscribed',
    'removed',
]);
/**
 * Who a campaign goes to.
 *
 * `all` is the whole guest list. `attendees` is an explicit subset. `status`
 * selects by computed lifecycle status — `{ mode: 'status', statuses: ['joined'] }`
 * is "everyone who has onboarded into the app".
 *
 * None of the three can reach a suppressed attendee, and no filter overrides
 * that — suppression runs after selection, server-side. TWO records decide it:
 * the attendee's own computed status (unsubscribed, bounced, complained,
 * revoked), and this event's campaign send history, so a hard bounce or a spam
 * complaint against an earlier campaign keeps that person off the next one too.
 * The second is not visible in the attendee's status by design (ADR-0009), which
 * is exactly why it has to be read separately.
 */
export const RecipientFilterSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('all') }),
    z.object({
        mode: z.literal('attendees'),
        attendeeIds: z.array(z.string().uuid()).min(1).max(5000),
    }),
    z.object({
        mode: z.literal('status'),
        statuses: z.array(CampaignAttendeeStatusSchema).min(1),
    }),
]);
export const CreateCampaignRequestSchema = z.object({
    name: z.string().min(1).max(200),
    templateId: z.string().uuid(),
    /** The immutable version to send. What went out must stay reproducible. */
    templateVersionId: z.string().uuid(),
    /** Required when the template uses `{{ form_link }}`; null otherwise. */
    formId: z.string().uuid().nullable().default(null),
    /** Fills `{{ session_name }}`. Empty renders empty. */
    sessionName: z.string().max(200).default(''),
    recipientFilter: RecipientFilterSchema.default({ mode: 'all' }),
});
export const PreviewRecipientsRequestSchema = z.object({
    recipientFilter: RecipientFilterSchema,
});
/**
 * Preview a campaign as one recipient will see it, and send a test to yourself.
 *
 * `attendeeId` chooses whose merge values to render — never who receives
 * anything. A test send goes to the caller's own verified address, read from
 * the authenticated principal and never from this body. Omit it and the render
 * uses labelled sample values, so the composer is legible before anybody has
 * been picked.
 *
 * `.strict()` on both: a body carrying `to`, `email`, or `recipient` is a
 * misunderstanding about where a test send goes, and a loud 400 is a better
 * answer than silently ignoring it.
 */
export const PreviewCampaignRequestSchema = z
    .object({ attendeeId: z.string().uuid().optional() })
    .strict();
export const TestSendCampaignRequestSchema = z
    .object({ attendeeId: z.string().uuid().optional() })
    .strict();
/** What a campaign preview returns — the same shape the send path produces. */
export const RenderedCampaignSchema = z.object({
    subject: z.string(),
    html: z.string(),
    text: z.string(),
    /**
     * MIME headers the message carries — today, `List-Unsubscribe`. Part of the
     * render rather than something the sender attaches, so the header names the
     * same address as the unsubscribe link in the rendered footer.
     */
    headers: z.array(z.object({ name: z.string(), value: z.string() })).default([]),
});
//# sourceMappingURL=campaigns.js.map